import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit
} from "@angular/core";

import { CardModule } from "primeng/card";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { ZDictionaryPipe } from "@zaeper/angular-localization-lib";
import {
  ESeverity,
  IToastService,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import {
  twoFactorRestClientInjectionToken,
  ITwoFactorRestClient,
  TwoFactorSetupComponent,
  TwoFactorManageComponent
} from "@zaeper/angular-2fa-lib";
import { Subscription } from "rxjs";
import { dictionaryServiceInjectionToken } from "@zaeper/angular-localization-lib";
import { DictionaryService } from "@zaeper/localization-lib";

@Component({
  selector: "app-two-factor-tab",
  templateUrl: "./two-factor-tab.component.html",
  styleUrl: "./two-factor-tab.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CardModule,
    ProgressSpinnerModule,
    ZDictionaryPipe,
    TwoFactorSetupComponent,
    TwoFactorManageComponent
]
})
export class TwoFactorTabComponent implements OnInit, OnDestroy {
  private readonly _subscriptions: Subscription = new Subscription();

  public loading: boolean = true;
  public twoFactorEnabled: boolean = false;
  public errorLoading: boolean = false;

  public labels: Record<string, string> = {};

  constructor(
    @Inject(twoFactorRestClientInjectionToken) private readonly _twoFactorRestClient: ITwoFactorRestClient,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    @Inject(dictionaryServiceInjectionToken) private readonly _dictionaryService: DictionaryService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this._loadLabels();
    this._loadStatus();
  }

  public ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  public onSetupComplete(): void {
    this.twoFactorEnabled = true;
    this._cdr.markForCheck();
    this._toastService.setMessage({
      title: this.labels["2fa-success"] || "Success",
      detail: this.labels["2fa-setup-success-message"] || "Two-factor authentication has been enabled successfully.",
      severity: ESeverity.success
    });
  }

  public onSetupError(errorMessage: string): void {
    this._toastService.setMessage({
      title: this.labels["2fa-error"] || "Error",
      detail: errorMessage,
      severity: ESeverity.error
    });
  }

  public onDisabled(): void {
    this.twoFactorEnabled = false;
    this._cdr.markForCheck();
    this._toastService.setMessage({
      title: this.labels["2fa-success"] || "Success",
      detail: this.labels["2fa-disable-success-message"] || "Two-factor authentication has been disabled.",
      severity: ESeverity.success
    });
  }

  public onCodesRegenerated(_codes: string[]): void {
    this._toastService.setMessage({
      title: this.labels["2fa-success"] || "Success",
      detail: this.labels["2fa-regenerate-success-message"] || "Recovery codes have been regenerated.",
      severity: ESeverity.success
    });
  }

  private _loadStatus(): void {
    this.loading = true;
    this.errorLoading = false;
    this._cdr.markForCheck();

    this._twoFactorRestClient.getStatus()
      .then(status => {
        this.twoFactorEnabled = status.enabled;
        this.loading = false;
        this._cdr.markForCheck();
      })
      .catch(() => {
        this.errorLoading = true;
        this.loading = false;
        this._cdr.markForCheck();
        this._toastService.setMessage({
          title: this.labels["2fa-error"] || "Error",
          detail: this.labels["2fa-status-error-message"] || "Could not load two-factor authentication status.",
          severity: ESeverity.error
        });
      });
  }

  private _loadLabels(): void {
    const keys: string[] = [
      "2fa-setup-description",
      "2fa-scan-qr-description",
      "2fa-verify-description",
      "2fa-recovery-codes-title",
      "2fa-recovery-codes-description",
      "2fa-manual-entry",
      "2fa-begin-setup",
      "2fa-next",
      "2fa-verify",
      "2fa-back-to-qr",
      "2fa-done",
      "2fa-copy",
      "2fa-download",
      "2fa-copied",
      "2fa-required-field",
      "2fa-invalid-code",
      "2fa-status-title",
      "2fa-enabled-status",
      "2fa-disable",
      "2fa-regenerate-codes",
      "2fa-disable-dialog-title",
      "2fa-disable-description",
      "2fa-regenerate-dialog-title",
      "2fa-regenerate-description",
      "2fa-confirm-disable",
      "2fa-confirm-regenerate",
      "2fa-new-recovery-codes-title",
      "2fa-success",
      "2fa-error",
      "2fa-setup-success-message",
      "2fa-disable-success-message",
      "2fa-regenerate-success-message",
      "2fa-status-error-message"
    ];

    this._subscriptions.add(
      this._dictionaryService.getDictionary("main").subscribe((dictionary: Map<string, string>) => {
        if (dictionary) {
          keys.forEach(key => {
            const value: string | undefined = dictionary.get(key);
            if (value) {
              this.labels[key] = value;
            }
          });
          this._cdr.markForCheck();
        }
      })
    );
  }
}
